import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faClose, faXmark, faCheckDouble, faTriangleExclamation, faBoxesStacked, faClock, faCalendarDays } from '@fortawesome/free-solid-svg-icons';
import NotificationRenderer from './NotificationRenderer';
import NotificationSocket from './NotificationSocket';
import { formatRelativeDate } from '../../utils/FormatDate';
import axios from 'axios';
import { API_BASE_URL } from '../../api';
import { useAuth } from '../../context/AuthProvider';

import './Bell.scss';

const getNotifType = (notification) => {
    const code = notification?.code?.toLowerCase() || '';
    if (code === 'low_stock' || code === 'overstock') return 'stock';
    if (code === 'batch_expiration') return 'expiration';
    if (code === 'appointment_reminder') return 'calendar';
    return 'default';
};

// Destination au clic sur une notification
const getNotifTarget = (notification) => {
    const code = notification?.code?.toLowerCase() || '';
    const d = notification?.data ?? {};
    if (code === 'low_stock' || code === 'overstock' || code === 'batch_expiration') {
        return d.productId ? `/products/${d.productId}` : null;
    }
    if (code === 'appointment_reminder') {
        return d.appointmentId ? `/Appointements?appointmentId=${d.appointmentId}` : '/Appointements';
    }
    if (code === 'inventaire_tournant') {
        return d.inventaireId ? `/inventaire/${d.inventaireId}` : '/inventaire';
    }
    return null;
};

const Bell = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const pharmacyId = user?.pharmacy?.id;
    const [isVisible, setIsVisible] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const [total, setTotal] = useState(0);
    const [markingAll, setMarkingAll] = useState(false);
    const containerRef = useRef(null);
    const listRef = useRef(null);

    const closeOffcanvas = () => { setIsAnimating(false); setTimeout(() => setIsVisible(false), 300); };
    const openOffcanvas = () => {
        setIsVisible(true);
        setTimeout(() => {
            setIsAnimating(true);
            if (items.length === 0) fetchNotifications(0, showAll);
        }, 10);
    };

    const fetchNotifications = (pageToLoad, all = false) => {
        if (loading) return;
        setLoading(true);
        axios.get(`${API_BASE_URL}/api/v1/notifications/getNotifications?page=${pageToLoad}&size=10&all=${all}`, { withCredentials: true })
            .then(response => {
                const newItems = response.data.content;
                setItems(prev => pageToLoad === 0 ? newItems : [...prev, ...newItems]);
                setHasMore(!response.data.last);
                setPage(pageToLoad + 1);
                setTotal(response.data.totalElements);
            })
            .catch(error => console.error('Failed to fetch notifications:', error))
            .finally(() => setLoading(false));
    };

    const fetchTotalCount = (all = false) => {
        axios.get(`${API_BASE_URL}/api/v1/notifications/getNotifications?page=0&size=1&all=${all}`, { withCredentials: true })
            .then(response => setTotal(response.data.totalElements))
            .catch(error => console.error('Failed to fetch notification count:', error));
    };

    const markAsRead = (id) => {
        axios.post(`${API_BASE_URL}/api/v1/notifications/markAsRead/${id}`, {}, { withCredentials: true })
            .then(() => { setItems(prev => prev.filter(item => item.id !== id)); setTotal(prev => prev - 1); })
            .catch(err => console.error('Failed to mark notification as read:', err));
    };

    const markAllAsRead = () => {
        if (markingAll) return;
        setMarkingAll(true);
        axios.post(`${API_BASE_URL}/api/v1/notifications/markAllAsRead`, {}, { withCredentials: true })
            .then(() => {
                if (showAll) {
                    // History ("Historique") mode keeps read items visible — just clear their unread state.
                    setItems(prev => prev.map(item => ({ ...item, isRead: true })));
                } else {
                    setItems([]);
                }
                setTotal(0);
            })
            .catch(err => console.error('Failed to mark all notifications as read:', err))
            .finally(() => setMarkingAll(false));
    };

    const handleScroll = useCallback(() => {
        if (!listRef.current || loading || !hasMore) return;
        const { scrollTop, scrollHeight, clientHeight } = listRef.current;
        if (scrollHeight - scrollTop - clientHeight < 50) fetchNotifications(page, showAll);
    }, [loading, hasMore, page, showAll]);

    useEffect(() => { fetchTotalCount(showAll); }, []);

    useEffect(() => {
        if (isVisible && listRef.current) {
            const el = listRef.current;
            el.addEventListener('scroll', handleScroll);
            return () => el.removeEventListener('scroll', handleScroll);
        }
    }, [isVisible, handleScroll]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) closeOffcanvas();
        };
        if (isVisible) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isVisible]);

    const toggleShowAll = () => {
        const newShowAll = !showAll;
        setShowAll(newShowAll);
        setPage(0);
        setItems([]);
        fetchNotifications(0, newShowAll);
        fetchTotalCount(newShowAll);
    };

    return (
        <>
            <button
                type="button"
                className={`bell-trigger ${total > 0 ? 'bell-trigger--active' : ''}`}
                onClick={openOffcanvas}
                data-tooltip-id="tooltip"
                data-tooltip-content="Alertes & Notifications"
            >
                <FontAwesomeIcon icon={faBell} />
                {total > 0 && <span className="bell-trigger__badge">{total}</span>}
            </button>

            <NotificationSocket
                pharmacyId={pharmacyId}
                onNotification={(notif) => {
                    if (!showAll) { setItems(prev => [notif, ...prev]); setTotal(prev => prev + 1); }
                }}
            />

            {isVisible && (
                <div ref={containerRef} className={`bell-panel ${isAnimating ? 'bell-panel--open' : 'bell-panel--closed'}`}>

                    {/* Header */}
                    <div className="bell-header">
                        <div className="bell-header__left">
                            <div className="bell-header__icon">
                                <FontAwesomeIcon icon={faBell} />
                            </div>
                            <div>
                                <p className="bell-header__title">Notifications</p>
                                <p className="bell-header__subtitle">{total} non lue{total > 1 ? 's' : ''}</p>
                            </div>
                        </div>
                        <div className="bell-header__actions">
                            {total > 0 && (
                                <button
                                    type="button"
                                    className="bell-header__mark-all"
                                    onClick={markAllAsRead}
                                    disabled={markingAll}
                                    title="Tout marquer comme lu"
                                    aria-label="Tout marquer comme lu"
                                >
                                    {markingAll
                                        ? <span className="spinner-border spinner-border-sm" />
                                        : <FontAwesomeIcon icon={faCheckDouble} />}
                                </button>
                            )}
                            <button type="button" className="bell-header__toggle" onClick={toggleShowAll}>
                                {showAll ? 'Récentes' : 'Historique'}
                            </button>
                        </div>
                    </div>

                    {/* List */}
                    <div className="bell-list" ref={listRef}>
                        {items.length === 0 && !loading ? (
                            <div className="bell-empty">
                                <FontAwesomeIcon icon={faBell} className="bell-empty__icon" />
                                <p className="bell-empty__title">Tout est à jour</p>
                                <p className="bell-empty__subtitle">Aucune notification non lue</p>
                            </div>
                        ) : (
                            <>
                                {items.map(notification => (
                                    <NotificationCard
                                        key={notification.id}
                                        notification={notification}
                                        type={getNotifType(notification)}
                                        onDismiss={showAll ? undefined : markAsRead /* the history view — nothing to dismiss */}
                                        onOpen={(notif) => {
                                            markAsRead(notif.id);
                                            const target = getNotifTarget(notif);
                                            if (target) {
                                                closeOffcanvas();
                                                navigate(target);
                                            }
                                        }}
                                    />
                                ))}
                            </>
                        )}

                        {loading && (
                            <div className={`bell-loading ${items.length === 0 ? 'bell-loading--centered' : ''}`}>
                                <div className="spinner-border text-primary" role="status" />
                            </div>
                        )}
                    </div>

                    {/* Close */}
                    <button type="button" onClick={closeOffcanvas} className="bell-close">
                        <FontAwesomeIcon icon={faClose} />
                    </button>
                </div>
            )}
        </>
    );
};

const CALENDAR_LABELS = {
    TASK:  'Rappel tâche',
    EVENT: 'Rappel événement',
};

// onDismiss: optional — when omitted (history view) the card has no dismiss button.
const NotificationCard = ({ notification, type, onDismiss, onOpen }) => {
    const isOverstock  = notification?.code?.toLowerCase() === 'overstock';
    const isStock      = type === 'stock';
    const isExpiration = type === 'expiration';
    const isCalendar   = type === 'calendar';

    const icon = isOverstock ? faBoxesStacked : isExpiration ? faClock : isCalendar ? faCalendarDays : faTriangleExclamation;

    const label = isOverstock  ? 'Surstock'
                : isStock      ? 'Stock faible'
                : isExpiration ? 'Péremption imminente'
                : isCalendar   ? (CALENDAR_LABELS[notification?.data?.appointmentType] ?? 'Rappel service')
                : 'Notification';

    return (
        <div
            className={`bell-card bell-card--${type}${onDismiss ? ' bell-card--dismissable' : ''}`}
            onClick={() => onOpen?.(notification)}
            style={{ cursor: 'pointer' }}
        >
            <div className={`bell-card__icon bell-card__icon--${type}`}>
                <FontAwesomeIcon icon={icon} />
            </div>
            <div className="bell-card__content">
                <p className="bell-card__label">{label}</p>
                <NotificationRenderer notification={notification} />
                {notification.createdAt && (
                    <p className="bell-card__time">{formatRelativeDate(notification.createdAt)}</p>
                )}
            </div>
            {/* dismisses the notification — backed by mark-as-read, which drops it from the unread list */}
            {onDismiss && (
                <button
                    type="button"
                    className={`bell-card__dismiss bell-card__dismiss--${type}`}
                    onClick={(e) => { e.stopPropagation(); onDismiss(notification.id); }}
                    title="Ignorer"
                    aria-label="Ignorer"
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            )}
        </div>
    );
};

export default Bell;